import { Grid, Stack, Tab, Tabs } from '@mui/material';
import { useAtom, useAtomValue } from 'jotai';
import { useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router';

import { LargeCard } from '@/components/LargeCard';
import LoadingIndicator from '@/components/LoadingIndicator';
import AssetRiskScoreDialog from '@/features/AssetRiskScoreDialog/AssetRiskScoreDialog';
import CaseAlertDetails from '@/features/CaseAlertDetails';
import CaseAssetDetails from '@/features/CaseAssetDetails';
import { CaseDetailsAI } from '@/features/CaseDetailsAI';
import { CaseDetailsTurbine } from '@/features/CaseDetailsTurbine';
import CaseFeedback from '@/features/CaseFeedback';
import { CaseRelevantData } from '@/features/CaseRelevantData';
import CaseSocReview from '@/features/CaseSocReview';
import CaseSubTopbarBottom from '@/features/CaseSubTopbarBottom';
import CaseSubTopbarTop from '@/features/CaseSubTopbarTop';
import TicketResponseActions from '@/features/TicketResponseActions';
import MainContent from '@/layout/MainContent';

import { assetRiskScoreDialogAtom } from '../Assets';
import { CASE_TABS, INTERNAL_CASE_TABS } from './Case.constants';
import { caseDetailsAtom, useFetchCaseDetails } from './Case.store';

const Case = () => {
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState<number>(0);
  const caseDetails = useAtomValue(caseDetailsAtom);
  const [assetRiskScoreDialog] = useAtom(assetRiskScoreDialogAtom);
  const { pathname } = useLocation();

  const { isLoading } = useFetchCaseDetails(id as string);
  const isInternalCasePage = pathname.includes('/case-internal');

  const caseTabs = useMemo(() => {
    if (isInternalCasePage) {
      return INTERNAL_CASE_TABS;
    }
    if (caseDetails?.ticket === null) {
      return CASE_TABS.filter((tab) => tab.label !== 'Ticket Details');
    }
    return CASE_TABS;
  }, [pathname, caseDetails]);

  const rightBoxesVisible =
    caseDetails?.ai_status === 'completed' ||
    isInternalCasePage ||
    caseDetails?.asset ||
    caseDetails?.soc_case_extra_details?.enrichment?.affected_hostname ||
    Boolean(caseDetails?.response_actions?.length);

  if (isLoading) {
    return <LoadingIndicator isLoading={isLoading} />;
  }

  return (
    <>
      <Stack sx={{ flex: 1, overflow: 'hidden' }}>
        <CaseSubTopbarTop />
        <CaseSubTopbarBottom />
        <MainContent>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, lg: rightBoxesVisible ? 8 : 12 }}>
              <LargeCard
                title={
                  <Tabs
                    value={activeTab}
                    onChange={(_: React.SyntheticEvent, newValue: number) =>
                      setActiveTab(newValue)
                    }
                    sx={{ ml: '-1rem' }}
                  >
                    {caseTabs.map((tab) => (
                      <Tab
                        key={tab.label}
                        disableRipple
                        sx={{ p: 2, minWidth: ['auto', '14rem'] }}
                        label={tab.label}
                        value={tab.value}
                      />
                    ))}
                  </Tabs>
                }
              >
                {activeTab === 0 && <CaseDetailsAI />}
                {isInternalCasePage && activeTab === 1 && <CaseRelevantData />}
                {activeTab === 2 && <CaseDetailsTurbine />}
              </LargeCard>
            </Grid>
            <Grid
              size={{ xs: 12, lg: 4 }}
              sx={{
                order: { xs: -1, lg: 1 },
                alignItems: 'center',
                justifyContent: 'flex-end'
              }}
            >
              <Stack spacing={2}>
                <CaseFeedback />
                {isInternalCasePage && <CaseSocReview />}
                {caseDetails?.response_actions &&
                  caseDetails?.response_actions?.length > 0 && (
                    <TicketResponseActions />
                  )}
                {(caseDetails?.asset ||
                  caseDetails?.soc_case_extra_details?.enrichment
                    ?.affected_hostname) && <CaseAssetDetails />}
                {isInternalCasePage && <CaseAlertDetails />}
              </Stack>
            </Grid>
          </Grid>
        </MainContent>
      </Stack>
      {assetRiskScoreDialog && <AssetRiskScoreDialog />}
    </>
  );
};

export default Case;
